import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import ItemThumbnail from '../../../components/ui/ItemThumbnail';
import OrderStatusBadge from './OrderStatusBadge';
import PaymentSummary from '../../../components/payments/PaymentSummary';
import ManualPaymentModal from '../../../components/payments/ManualPaymentModal';
import { useAuth } from '../../../components/context/AuthContext';
import { canPayAndCompleteManually, canRecordManualPayment } from '../../../components/payments/manualPaymentUtils';

const STATUS_ACTIONS = {
  pending: [
    { label: 'Start Processing', status: 'processing', variant: 'default' },
    { label: 'Cancel Order', status: 'cancelled', variant: 'destructive' },
  ],
  processing: [
    { label: 'Mark Ready for Pickup', status: 'readyForPickup', variant: 'default' },
    { label: 'Cancel Order', status: 'cancelled', variant: 'destructive' },
  ],
  readyforpickup: [
    { label: 'Mark Completed', status: 'completed', variant: 'default' },
    { label: 'Cancel Order', status: 'cancelled', variant: 'destructive' },
  ],
  completed: [],
  cancelled: [],
};

const normalizeStatus = (status) => String(status || '').toLowerCase().replace(/[\s_-]/g, '');

const formatDateTime = (value) => {
  if (!value) return 'Not provided';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatAmount = (value) => `GH\u00A2${(Number(value) || 0).toFixed(2)}`;

const formatReservationTime = (expiresAt, now) => {
  if (!expiresAt) return '';
  const remaining = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(remaining) || remaining <= 0) return 'Reservation expired';
  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours > 0 ? `${hours}h ` : ''}${minutes}m ${seconds}s remaining`;
};

const OrderDetailsModal = ({ booking, onClose, onStatusChange, onPaymentRecorded }) => {
  const { user } = useAuth();
  const [pendingActionStatus, setPendingActionStatus] = useState(null);
  const [paymentRecord, setPaymentRecord] = useState(null);
  const [isManualPaymentOpen, setIsManualPaymentOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const orderData = Array.isArray(booking) ? booking[0] : booking;

  useEffect(() => {
    setPendingActionStatus(null);
    setPaymentRecord(orderData);
    setIsManualPaymentOpen(false);
  }, [orderData?.id, orderData?.status, orderData?.amountPaid, orderData?.balanceDue, orderData?.isFullyPaid]);

  useEffect(() => {
    if (!orderData?.stockReservationExpiresAt) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [orderData?.stockReservationExpiresAt]);

  useEffect(() => {
    if (!orderData) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !pendingActionStatus) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, orderData, pendingActionStatus]);

  const items = useMemo(() => orderData?.items || [], [orderData?.items]);

  if (!orderData) return null;

  const normalizedStatus = normalizeStatus(orderData.status);
  const subtotal = items.reduce((sum, item) => {
    const declaredLineTotal = Number(item.totalPrice);
    const calculatedLineTotal = (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0);
    return sum + (item.totalPrice != null && Number.isFinite(declaredLineTotal) ? declaredLineTotal : calculatedLineTotal);
  }, 0);
  const declaredTotal = Number(orderData.totalAmount);
  const total = orderData.totalAmount != null && Number.isFinite(declaredTotal) ? declaredTotal : subtotal;
  const isAdmin = user?.roles?.some((role) => String(role).toLowerCase() === 'admin');
  const availableActions = isAdmin ? (STATUS_ACTIONS[normalizedStatus] || []) : [];
  const canRecordBalance = canRecordManualPayment({
    isAdmin,
    record: paymentRecord,
    fallbackRequiresPayment: user?.company?.requiresPayment,
  });
  const canPayAndComplete = canRecordBalance && canPayAndCompleteManually({
    sourceType: 'order',
    status: paymentRecord?.status ?? orderData.status,
  });
  const showStockReservation = Boolean(orderData.stockReservationExpiresAt && !paymentRecord?.isFullyPaid);
  const hasSupplementaryDetails = showStockReservation || Boolean(orderData.notes);

  const handleActionClick = async (status) => {
    if (pendingActionStatus) return;
    setPendingActionStatus(status);
    try {
      await onStatusChange(orderData.id, status);
    } finally {
      setPendingActionStatus(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-3 backdrop-blur-sm sm:p-4"
      onMouseDown={(event) => event.target === event.currentTarget && !pendingActionStatus && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-details-title"
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
      >
        <header className="flex flex-shrink-0 items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
          <div>
            <h2 id="order-details-title" className="font-heading text-xl font-semibold text-foreground">Order Details</h2>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <p className="text-xs text-muted-foreground font-caption">#{orderData.referenceCode}</p>
              <OrderStatusBadge status={normalizedStatus} compact />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={Boolean(pendingActionStatus)}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="Close order details"
          >
            <Icon name="X" size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar sm:p-6">
          <div className="space-y-6">
            <div className="grid items-stretch gap-4 md:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)]">
              <section className="rounded-2xl bg-muted/30 p-4 sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground font-caption">Customer information</p>
                  <h3 className="mt-1 font-heading text-2xl font-semibold text-foreground">{orderData.clientName}</h3>
                  <div className="mt-3 flex flex-col gap-2 text-sm text-foreground font-caption sm:flex-row sm:flex-wrap sm:gap-x-5">
                    <span className="flex items-center gap-2">
                      <Icon name="Phone" size={15} className="flex-shrink-0 text-muted-foreground" />
                      <span>{orderData.clientPhone || 'Phone not provided'}</span>
                    </span>
                    {orderData.clientEmail && (
                      <span className="flex min-w-0 items-center gap-2">
                        <Icon name="Mail" size={15} className="flex-shrink-0 text-muted-foreground" />
                        <span className="break-all">{orderData.clientEmail}</span>
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground font-caption">
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                    {orderData.date ? ` \u00B7 Placed ${formatDateTime(orderData.date)}` : ''}
                  </p>
                </div>
              </div>
              </section>

              <section className="rounded-2xl bg-muted/30 p-4 sm:p-5">
                <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Payment</h4>
                <PaymentSummary
                  record={paymentRecord}
                  totalAmount={total}
                  onRecordBalance={canRecordBalance ? () => setIsManualPaymentOpen(true) : undefined}
                />
              </section>
            </div>

            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.75fr)]">
              <div className="space-y-6">
                <section>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Ordered items</h4>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground font-caption font-medium">{items.length}</span>
                  </div>

                  {items.length > 0 ? (
                    <div className="divide-y divide-border rounded-xl bg-muted/20 px-3">
                      {items.map((item, index) => {
                        const quantity = Number(item.quantity) || 0;
                        const unitPrice = Number(item.unitPrice) || 0;
                        const declaredLineTotal = Number(item.totalPrice);
                        const lineTotal = item.totalPrice != null && Number.isFinite(declaredLineTotal) ? declaredLineTotal : unitPrice * quantity;

                        return (
                          <article key={`${item.serviceItemId || item.itemName || item.name || 'item'}-${index}`} className="flex items-center gap-3 py-2.5">
                            <ItemThumbnail src={item.imageUrl} alt={item.itemName || item.name} size="sm" fallbackIcon="Package" />
                            <div className="min-w-0 flex-1">
                              <p className="mb-1 text-sm font-semibold text-foreground font-caption">{item.itemName || item.name || 'Unnamed item'}</p>
                              <p className="text-xs text-muted-foreground font-caption">Product &middot; Qty {quantity}</p>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <p className="text-sm font-semibold text-foreground font-caption">{formatAmount(lineTotal)}</p>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-xl bg-muted/30 p-6 text-center text-sm text-muted-foreground font-caption">
                      No items are available for this order.
                    </div>
                  )}
                </section>

                <section>
                  <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Order history</h4>
                  {orderData.history?.length > 0 ? (
                    <ol className="space-y-0 px-1 py-2">
                      {orderData.history.map((event, index) => (
                        <li key={`${event.timestamp}-${index}`} className="relative flex gap-3 pb-5 last:pb-0">
                          {index < orderData.history.length - 1 && (
                            <span className="absolute left-[5px] top-4 h-[calc(100%-0.25rem)] w-px bg-border" aria-hidden="true" />
                          )}
                          <span className="relative z-10 mt-1.5 h-3 w-3 flex-shrink-0 rounded-full border-2 border-card bg-primary" />
                          <div className="min-w-0">
                            <p className="text-sm text-foreground font-caption font-medium">{event.action}</p>
                            <p className="mt-1 text-xs text-muted-foreground font-caption">{formatDateTime(event.timestamp)}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="rounded-xl bg-muted/30 p-5 text-center text-sm text-muted-foreground font-caption">
                      No order history yet.
                    </div>
                  )}
                </section>
              </div>

              {hasSupplementaryDetails && (
                <aside className="space-y-5">
                {showStockReservation && (
                  <section className="rounded-xl bg-warning/10 p-4">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-warning font-caption">Stock reservation</h4>
                    <p className="mt-2 text-sm font-medium text-foreground font-caption">
                      {formatReservationTime(orderData.stockReservationExpiresAt, now)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground font-caption">
                      Reserved until {formatDateTime(orderData.stockReservationExpiresAt)}. Stock is not guaranteed after expiry.
                    </p>
                  </section>
                )}

                {orderData.notes && (
                  <section className="rounded-xl bg-muted/25 p-4">
                    <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Customer notes</h4>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground font-caption">{orderData.notes}</p>
                  </section>
                )}
                </aside>
              )}
            </div>
          </div>
        </div>

        <footer className="flex flex-shrink-0 flex-col-reverse gap-3 border-t border-border bg-card px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button type="button" variant="outline" onClick={onClose} disabled={Boolean(pendingActionStatus)}>
            Close
          </Button>
          {availableActions.map((action) => (
            <Button
              key={action.status}
              variant={action.variant}
              onClick={() => handleActionClick(action.status)}
              loading={pendingActionStatus === action.status}
              disabled={Boolean(pendingActionStatus) && pendingActionStatus !== action.status}
            >
              {action.label}
            </Button>
          ))}
        </footer>
      </div>

      <ManualPaymentModal
        isOpen={isManualPaymentOpen}
        onClose={() => setIsManualPaymentOpen(false)}
        sourceType="order"
        sourceId={orderData.id}
        balanceDue={paymentRecord?.balanceDue}
        canPayAndComplete={canPayAndComplete}
        onRecorded={async (result) => {
          const updated = { ...paymentRecord, ...result };
          setPaymentRecord(updated);
          await onPaymentRecorded?.(orderData.id, result);
        }}
      />
    </div>
  );
};

export default OrderDetailsModal;
